import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  PlatformBmuManagementResponse,
  PlatformBmuLifecycleResponseEnvelope,
  PlatformBillingLedgerResponse,
  PlatformBillingHistoryResponse,
  PlatformBillingExecutionPreviewResponse,
  PlatformBillingPlanAssignmentPayload,
  PlatformBillingPlanHistoryResponse,
  PlatformBillingPlanAssignmentResponse,
  PlatformBillingOverviewResponse,
  PlatformBillingPreviewResponse,
  PlatformBillingCommercialPipelineResponse,
  PlatformBillingCommercialStateResponse,
  PlatformBillingAvailablePlansForNewSchoolResponse,
  PlatformBillingFailurePolicyPayload,
  PlatformBillingFailurePolicyResponse,
  PlatformBillingChargeNowReadinessResponse,
  PlatformBillingChargeNowResponse,
  PlatformBillingManualChargeResponse,
  PlatformBillingManualChargeBeginResponse,
  PlatformBillingManualChargePaymentSummaryResponse,
} from '../../models/platform-billing-preview';

@Injectable()
export class PlatformBillingPreviewService {
  constructor(private httpClient: HttpClient) {}

  getOverview(): Observable<PlatformBillingOverviewResponse> {
    return this.httpClient.get<PlatformBillingOverviewResponse>(
      'admin/platform-billing/overview'
    );
  }

  getCommercialPipeline(): Observable<PlatformBillingCommercialPipelineResponse> {
    return this.httpClient.get<PlatformBillingCommercialPipelineResponse>(
      'admin/school-commercial-pipeline'
    );
  }

  getCommercialState(state: string): Observable<PlatformBillingCommercialStateResponse> {
    return this.httpClient.get<PlatformBillingCommercialStateResponse>(
      `admin/school-commercial-states/${state}`
    );
  }

  getHistory(schoolId?: number | string, billingPeriodId?: number | string): Observable<PlatformBillingHistoryResponse> {
    const params: string[] = [];

    if (schoolId !== null && schoolId !== undefined && schoolId !== '') {
      params.push('school_id=' + encodeURIComponent(String(schoolId)));
    }

    if (billingPeriodId !== null && billingPeriodId !== undefined && billingPeriodId !== '') {
      params.push('billing_period_id=' + encodeURIComponent(String(billingPeriodId)));
    }

    const query = params.length ? '?' + params.join('&') : '';

    return this.httpClient.get<PlatformBillingHistoryResponse>(
      'admin/platform-billing/history' + query
    );
  }

  getLedger(
    schoolId?: number | string,
    billingPeriodId?: number | string,
    eventType?: string,
  ): Observable<PlatformBillingLedgerResponse> {
    const params: string[] = [];

    if (schoolId !== null && schoolId !== undefined && schoolId !== '') {
      params.push('school_id=' + encodeURIComponent(String(schoolId)));
    }

    if (billingPeriodId !== null && billingPeriodId !== undefined && billingPeriodId !== '') {
      params.push('billing_period_id=' + encodeURIComponent(String(billingPeriodId)));
    }

    if (eventType !== null && eventType !== undefined && eventType !== '') {
      params.push('event_type=' + encodeURIComponent(String(eventType)));
    }

    const query = params.length ? '?' + params.join('&') : '';

    return this.httpClient.get<PlatformBillingLedgerResponse>(
      'admin/platform-billing/ledger' + query
    );
  }

  getExecutionPreview(): Observable<PlatformBillingExecutionPreviewResponse> {
    return this.httpClient.get<PlatformBillingExecutionPreviewResponse>(
      'admin/platform-billing/execution-preview'
    );
  }

  getPreview(calculationId: number | string): Observable<PlatformBillingPreviewResponse> {
    return this.httpClient.get<PlatformBillingPreviewResponse>(
      `admin/platform-billing/calculations/${calculationId}/preview`
    );
  }

  getAvailablePlansForNewSchool(): Observable<PlatformBillingAvailablePlansForNewSchoolResponse> {
    return this.httpClient.get<PlatformBillingAvailablePlansForNewSchoolResponse>(
      'admin/platform-billing/plans'
    );
  }

  getPlanAssignment(schoolId: number | string): Observable<PlatformBillingPlanAssignmentResponse> {
    return this.httpClient.get<PlatformBillingPlanAssignmentResponse>(
      `admin/platform-billing/schools/${schoolId}/plan-assignment`
    );
  }

  getPlanHistory(schoolId: number | string): Observable<PlatformBillingPlanHistoryResponse> {
    return this.httpClient.get<PlatformBillingPlanHistoryResponse>(
      `admin/platform-billing/schools/${schoolId}/plan-history`
    );
  }

  getSchoolBmus(schoolId: number | string): Observable<PlatformBmuManagementResponse> {
    return this.httpClient.get<PlatformBmuManagementResponse>(
      `admin/platform-billing/schools/${schoolId}/bmus`
    );
  }

  suspendBmu(bmuId: number | string): Observable<PlatformBmuLifecycleResponseEnvelope> {
    return this.httpClient.post<PlatformBmuLifecycleResponseEnvelope>(
      `admin/platform-billing/bmus/${bmuId}/suspend`,
      {}
    );
  }

  resumeBmu(bmuId: number | string): Observable<PlatformBmuLifecycleResponseEnvelope> {
    return this.httpClient.post<PlatformBmuLifecycleResponseEnvelope>(
      `admin/platform-billing/bmus/${bmuId}/resume`,
      {}
    );
  }

  assignPlan(
    schoolId: number | string,
    payload: PlatformBillingPlanAssignmentPayload
  ): Observable<PlatformBillingPlanAssignmentResponse> {
    return this.httpClient.post<PlatformBillingPlanAssignmentResponse>(
      `admin/platform-billing/schools/${schoolId}/plan-assignment`,
      payload
    );
  }

  getFailurePolicy(): Observable<PlatformBillingFailurePolicyResponse> {
    return this.httpClient.get<PlatformBillingFailurePolicyResponse>(
      'admin/platform-billing/failure-policy'
    );
  }

  updateFailurePolicy(
    payload: PlatformBillingFailurePolicyPayload
  ): Observable<PlatformBillingFailurePolicyResponse> {
    return this.httpClient.put<PlatformBillingFailurePolicyResponse>(
      'admin/platform-billing/failure-policy',
      payload
    );
  }

  getSchoolChargeNowReadiness(schoolId: number | string): Observable<PlatformBillingChargeNowReadinessResponse> {
    return this.httpClient.get<PlatformBillingChargeNowReadinessResponse>(
      `admin/platform-billing/schools/${schoolId}/charge-now-readiness`
    );
  }

  chargeCalculationNow(calculationId: number | string): Observable<PlatformBillingChargeNowResponse> {
    return this.httpClient.post<PlatformBillingChargeNowResponse>(
      `admin/platform-billing/calculations/${calculationId}/charge-now`,
      {}
    );
  }

  retryCalculationNow(calculationId: number | string): Observable<PlatformBillingChargeNowResponse> {
    return this.httpClient.post<PlatformBillingChargeNowResponse>(
      `admin/platform-billing/calculations/${calculationId}/retry-now`,
      {}
    );
  }

  /**
   * MKC-78 #1 manual, ad-hoc Super Admin Charge Now for a School
   * (S11 Iteration 1584/1585) — NOT the scheduled-calculation Charge Now
   * above. `formData` must already contain description/amount/
   * message_to_payer/idempotency_key and an optional attachment file;
   * this method only owns the URL, so a future Student/PMU manual charge
   * can reuse submitManualCharge() directly with its own endpoint.
   */
  manualChargeSchool(schoolId: number | string, formData: FormData): Observable<PlatformBillingManualChargeResponse> {
    return this.submitManualCharge(`admin/platform-billing/schools/${schoolId}/manual-charge`, formData);
  }

  /**
   * MKC-78 S11 Iteration 1641 - safe brand/last4 summary of the
   * School's existing saved MKC Platform payment method (if any), used
   * only to decide whether the Charge Now dialog can offer the
   * saved-card option.
   */
  getSchoolManualChargePaymentSummary(schoolId: number | string): Observable<PlatformBillingManualChargePaymentSummaryResponse> {
    return this.httpClient.get<PlatformBillingManualChargePaymentSummaryResponse>(
      `admin/platform-billing/schools/${schoolId}/manual-charge/payment-summary`
    );
  }

  /**
   * MKC-78 S11 Iteration 1641 - PHASE A of the fresh, one-off card
   * Charge Now flow. Same field set as manualChargeSchool() above -
   * never a payer/School/Stripe customer/Stripe payment method id.
   */
  beginSchoolFreshCardCharge(schoolId: number | string, formData: FormData): Observable<PlatformBillingManualChargeBeginResponse> {
    return this.httpClient.post<PlatformBillingManualChargeBeginResponse>(
      `admin/platform-billing/schools/${schoolId}/manual-charge/begin`,
      formData
    );
  }

  /**
   * MKC-78 S11 Iteration 1641 - PHASE C. `chargeId` is the opaque id
   * already returned by beginSchoolFreshCardCharge() above.
   */
  finalizeSchoolFreshCardCharge(schoolId: number | string, chargeId: number): Observable<PlatformBillingManualChargeResponse> {
    return this.httpClient.post<PlatformBillingManualChargeResponse>(
      `admin/platform-billing/schools/${schoolId}/manual-charge/finalize`,
      { charge_id: chargeId }
    );
  }

  /**
   * Entity-agnostic multipart submission core for manual Charge Now
   * requests. Deliberately takes a plain URL rather than a School-typed
   * parameter so a later manualChargeStudent(studentId, formData) can
   * share this exact method. Never sets a Content-Type header itself —
   * the browser must set the multipart boundary for a FormData body
   * (see AuthInterceptor's `req.body instanceof FormData` handling).
   */
  private submitManualCharge(url: string, formData: FormData): Observable<PlatformBillingManualChargeResponse> {
    return this.httpClient.post<PlatformBillingManualChargeResponse>(url, formData);
  }
}
